import { useEffect } from "react";

/**
 * useSEO — vanilla per-page SEO. No react-helmet dependency needed.
 * Sets document title + rewrites/creates the <meta> tags we care about.
 * Also injects a JSON-LD structured-data block for rich Google results.
 */
export function useSEO({
  title,
  description,
  path = "",
  image,
  jsonLd,
  keywords,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, name, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", `https://flippilot.us${path}`);
    if (image) setMeta("property", "og:image", image);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `https://flippilot.us${path}`);

    // JSON-LD structured data (injected & cleaned per-page)
    const existing = document.head.querySelector(
      'script[data-managed-jsonld="true"]',
    );
    if (existing) existing.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-managed-jsonld", "true");
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, path, image, keywords, jsonLd]);
}

// Reusable JSON-LD for the whole business — pass on the Home page.
export const BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: "FlipPilot",
  url: "https://flippilot.us",
  logo: "https://flippilot.us/logo.png",
  description:
    "FlipPilot buys houses for cash across Southwest Florida. Fast, no-obligation offers with close dates as short as 14 days. We also partner with wholesalers and investors on off-market acquisition.",
  areaServed: {
    "@type": "State",
    name: "Florida",
  },
  address: {
    "@type": "PostalAddress",
    addressRegion: "FL",
    addressCountry: "US",
  },
  telephone: "+1-239-480-8400",
  email: "sales@flippilot.us",
  sameAs: [],
};
